import { ECSEvent, EventNameToType } from '../events';

/**
 * Service managing the event bus.
 * Extracted from State.events, State.deferredEvents, and State.tick.
 */
export class EventBusService {
  private events: ECSEvent[] = [];
  private deferredEvents: ECSEvent[] = [];
  private _tick: number = 0;

  /**
   * Get current tick number.
   */
  get tick(): number {
    return this._tick;
  }

  /**
   * Increment the tick counter. Called once per game loop iteration.
   * @returns The new tick value after incrementing.
   */
  public incrementTick(): number {
    return ++this._tick;
  }

  /**
   * Set the tick counter to a specific value.
   * Use this for syncing with a remote source (e.g. server).
   */
  public setTick(tick: number): void {
    this._tick = tick;
  }

  /**
   * Add an event to be processed this tick.
   */
  public addEvent(event: ECSEvent): void {
    this.events.push(event);
  }

  /**
   * Add an event to be processed on the NEXT tick.
   * Use this for events added during message processing.
   */
  public addDeferredEvent(event: ECSEvent): void {
    this.deferredEvents.push(event);
  }

  /**
   * Get all events of a specific type for this tick.
   */
  public getEventsOfType<T extends keyof EventNameToType>(name: T): EventNameToType[T][] {
    return this.events.filter((event) => event.name === name) as EventNameToType[T][];
  }

  /**
   * Query events matching any of the given names.
   */
  public queryEvents(names: ECSEvent['name'][]): ECSEvent[] {
    return this.events.filter((event) => names.includes(event.name));
  }

  /**
   * Clears current tick events and transfers deferred events to main bus.
   * Called at the start of each tick.
   */
  public resetEvents(): void {
    this.events = [...this.deferredEvents];
    this.deferredEvents = [];
  }

  /**
   * Reset the service (for testing).
   */
  public reset(): void {
    this.events = [];
    this.deferredEvents = [];
    this._tick = 0;
  }
}
